import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer, liftV4Props } from "./element-renderer"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
} from "../components/ui/pagination"
import { cn } from "../lib/utils"
import type { CogoElement } from "../core/types"
import { positiveInteger } from "./property-value-utils"

export function PaginationRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  // Note: child buttons use variant === "previous"/"next" as a legacy
  // role marker (see conditional chain below); the atom-defaults contract
  // applies to the outer <Pagination>, which is orthogonal.
  const atomDefaults = useAtomDefaults("pagination")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  // JITRULE Task 2: authored children carry v4-schema fields that may be
  // nested under `props` — lift before any downstream `.text` read.
  const explicitItems = (element.children ?? []).map(liftV4Props)
  const rawCurrent = (element as unknown as { currentPage?: unknown }).currentPage
  const rawTotal = (element as unknown as { totalPages?: unknown }).totalPages
  const currentPage = positiveInteger(typeof rawCurrent === "string" ? resolveSymbol(rawCurrent) : rawCurrent, 1, 10_000)
  const totalPages = positiveInteger(typeof rawTotal === "string" ? resolveSymbol(rawTotal) : rawTotal, 1, 100)
  const items: CogoElement[] = explicitItems.length > 0
    ? explicitItems
    : [
        { id: `${element.id}__previous`, type: "button", variant: "previous", text: "Previous", disabled: currentPage <= 1 },
        ...Array.from({ length: totalPages }, (_, index) => ({
          id: `${element.id}__page_${index + 1}`,
          type: "button",
          text: String(index + 1),
          variant: index + 1 === currentPage ? "default" : "outline",
        } as CogoElement)),
        { id: `${element.id}__next`, type: "button", variant: "next", text: "Next", disabled: currentPage >= totalPages },
      ]
  const hasAuthoredCurrentPage = rawCurrent !== undefined && rawCurrent !== null
  const pageItems = items.filter((child) => (
    child.type === "button"
    && child.variant !== "previous"
    && child.variant !== "next"
  ))

  const isActivePage = (child: CogoElement): boolean => {
    if (!hasAuthoredCurrentPage) return child.variant === "default"

    const pageValue = (child as unknown as { value?: unknown }).value ?? child.text
    const resolvedPageValue = typeof pageValue === "string" ? resolveSymbol(pageValue) : pageValue
    const numericPageValue = Number(resolvedPageValue)
    const pageNumber = Number.isInteger(numericPageValue) && numericPageValue > 0
      ? numericPageValue
      : pageItems.indexOf(child) + 1
    return pageNumber === currentPage
  }

  return (
    <Pagination
      className={cn(element.className)}
      data-testid={`pagination-${element.id}`}
      data-atom-type="pagination"
      data-total-pages={totalPages}
      data-variant={resolvedVariant}
      data-size={resolvedSize}
    >
      <PaginationContent>
        {items.map((child) => (
          <PaginationItem key={child.id}>
            {child.type === "button" ? (
              child.variant === "previous" ? (
                <PaginationPrevious
                  text={child.text ? String(resolveSymbol(child.text) ?? "Previous") : "Previous"}
                  onClick={() => dispatchEvent(child.id, "tap", { value: child.text ?? child.id })}
                  aria-disabled={child.disabled === true || undefined}
                />
              ) : child.variant === "next" ? (
                <PaginationNext
                  text={child.text ? String(resolveSymbol(child.text) ?? "Next") : "Next"}
                  onClick={() => dispatchEvent(child.id, "tap", { value: child.text ?? child.id })}
                  aria-disabled={child.disabled === true || undefined}
                />
              ) : (
                <PaginationLink
                  isActive={isActivePage(child)}
                  onClick={() => dispatchEvent(child.id, "tap", { value: child.text ?? child.id })}
                  aria-label={child.ariaLabel}
                >
                  {child.text ? String(resolveSymbol(child.text) ?? "") : ""}
                </PaginationLink>
              )
            ) : (
              <ElementRenderer element={child} />
            )}
          </PaginationItem>
        ))}
      </PaginationContent>
    </Pagination>
  )
}
